import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Moon, Sun, Menu, X, ChevronDown } from 'lucide-react';
import pidesk_logo from '../assets/pidesk_logo.png';
import { landingPages } from '../data/landing';
import './Navbar.css';

const SOLUTION_GROUPS = [
  { label: 'ERP Software', cluster: 'erp' },
  { label: 'Learning Platforms', cluster: 'learning' },
  { label: 'Development Services', cluster: 'services' }
].map((g) => ({ ...g, items: landingPages.filter((p) => p.cluster === g.cluster) }));

const Navbar = () => {
  const [isDark, setIsDark] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [activeDoc, setActiveDoc] = useState(0);
  const [mobileDocsOpen, setMobileDocsOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);
  const location = useLocation();
  const closeDocs = () => setDocsOpen(false);
  const closeSolutions = () => setSolutionsOpen(false);

  useEffect(() => {
    // Check local storage or system preference
    const isDarkMode = localStorage.getItem('theme') === 'dark' ||
      (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches);

    setIsDark(isDarkMode);
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
  }, []);

  useEffect(() => {
    // Lock background scroll while the mobile popup is open
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  const toggleTheme = () => {
    const newTheme = !isDark;
    setIsDark(newTheme);
    document.documentElement.setAttribute('data-theme', newTheme ? 'dark' : 'light');
    localStorage.setItem('theme', newTheme ? 'dark' : 'light');
  };

  const navLinks = [
    { name: 'Services', path: '/services' },
    { name: 'Products', path: '/products' },
    { name: 'Blog', path: '/blog' },
  ];

  const docs = [
    { name: 'About', path: '/about', desc: 'Who we are, what we believe in and how the Pidesk team builds digital products.' },
    { name: 'DCIC', path: '/dcic', desc: 'Developer Community of Innovation & Contribution: hackathons, events and incubation support.' },
    { name: 'Privacy Policy', path: '/privacy-policy', desc: 'What data we collect, how we use it and the choices you have over your information.' },
    { name: 'Terms & Conditions', path: '/terms-and-conditions', desc: 'The rules for using Pidesk services, including responsibilities and limitations.' },
  ];

  const docsActive = docs.some((d) => d.path === location.pathname);
  const solutionsActive = landingPages.some((p) => location.pathname === `/${p.slug}` || location.pathname.startsWith(`/${p.slug}/`));

  return (
    <header className={`navbar ${isMenuOpen ? 'menu-open' : ''}`}>
      <div className="nav-pill">
        <Link to="/" className="brand-logo" onClick={() => setIsMenuOpen(false)}>
          <img src={pidesk_logo} alt="Pidesk" className="brand-img" />
        </Link>

        {/* Desktop Nav */}
        <nav className="desktop-nav">
          <ul className="nav-list">
            {navLinks.map((link) => (
              <li key={link.name}>
                <Link
                  to={link.path}
                  className={`nav-link ${location.pathname === link.path || (link.path !== '/' && location.pathname.startsWith(link.path + '/')) ? 'active' : ''}`}
                >
                  {link.name}
                </Link>
              </li>
            ))}
            <li
              className="nav-dropdown"
              onMouseEnter={() => setSolutionsOpen(true)}
              onMouseLeave={closeSolutions}
              onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) closeSolutions(); }}
            >
              <button
                type="button"
                className={`nav-link nav-link-btn ${solutionsActive || solutionsOpen ? 'active' : ''}`}
                aria-expanded={solutionsOpen}
                aria-haspopup="true"
                onClick={() => setSolutionsOpen((o) => !o)}
              >
                Solutions <ChevronDown size={14} className={`chev ${solutionsOpen ? 'open' : ''}`} />
              </button>

              <div className={`mega-menu mega-menu-wide ${solutionsOpen ? 'open' : ''}`}>
                {SOLUTION_GROUPS.map((group) => (
                  <div key={group.label} className="mega-col">
                    <span className="mega-col-title">{group.label}</span>
                    <ul className="mega-col-list">
                      {group.items.map((p) => (
                        <li key={p.slug}>
                          <Link to={`/${p.slug}`} className="mega-col-link" onClick={closeSolutions}>
                            {p.keyword}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </li>
            <li
              className="nav-dropdown"
              onMouseEnter={() => setDocsOpen(true)}
              onMouseLeave={closeDocs}
              onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) closeDocs(); }}
            >
              <button
                type="button"
                className={`nav-link nav-link-btn ${docsActive || docsOpen ? 'active' : ''}`}
                aria-expanded={docsOpen}
                aria-haspopup="true"
                onClick={() => setDocsOpen((o) => !o)}
              >
                Docs <ChevronDown size={14} className={`chev ${docsOpen ? 'open' : ''}`} />
              </button>

              <div className={`mega-menu ${docsOpen ? 'open' : ''}`}>
                <ul className="mega-list">
                  {docs.map((d, i) => (
                    <li key={d.name}>
                      <Link
                        to={d.path}
                        className={`mega-item ${activeDoc === i ? 'active' : ''}`}
                        onMouseEnter={() => setActiveDoc(i)}
                        onFocus={() => setActiveDoc(i)}
                        onClick={closeDocs}
                      >
                        {d.name}
                      </Link>
                    </li>
                  ))}
                </ul>
                <div className="mega-preview">
                  <h4>{docs[activeDoc].name}</h4>
                  <p>{docs[activeDoc].desc}</p>
                  <Link to={docs[activeDoc].path} className="mega-link" onClick={closeDocs}>Open page &rarr;</Link>
                </div>
              </div>
            </li>
          </ul>
        </nav>

        <div className="nav-actions">
          <button onClick={toggleTheme} className="theme-toggle" aria-label="Toggle Theme">
            {isDark ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          <Link to="/contact" className="btn btn-primary contact-btn">Let's Talk</Link>

          <button
            className="mobile-menu-btn"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle Menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Nav: a scrollable popup, with a backdrop to dim the page and close on tap-away */}
      <div
        className={`mobile-nav-backdrop ${isMenuOpen ? 'open' : ''}`}
        onClick={() => setIsMenuOpen(false)}
        aria-hidden="true"
      />
      <div className={`mobile-nav ${isMenuOpen ? 'open' : ''}`}>
        <ul className="mobile-nav-list">
          {navLinks.map((link) => (
            <li key={link.name}>
              <Link
                to={link.path}
                className="mobile-nav-link"
                onClick={() => setIsMenuOpen(false)}
              >
                {link.name}
              </Link>
            </li>
          ))}
          <li>
            <button
              type="button"
              className="mobile-nav-link mobile-docs-btn"
              aria-expanded={mobileSolutionsOpen}
              onClick={() => setMobileSolutionsOpen((o) => !o)}
            >
              Solutions <ChevronDown size={18} className={`chev ${mobileSolutionsOpen ? 'open' : ''}`} />
            </button>
            {mobileSolutionsOpen && (
              <ul className="mobile-sub-list">
                {SOLUTION_GROUPS.map((group) => (
                  <li key={group.label} className="mobile-sub-group">
                    <small className="mobile-sub-group-title">{group.label}</small>
                    {group.items.map((p) => (
                      <Link key={p.slug} to={`/${p.slug}`} className="mobile-sub-link" onClick={() => setIsMenuOpen(false)}>
                        <span>{p.keyword}</span>
                      </Link>
                    ))}
                  </li>
                ))}
              </ul>
            )}
          </li>
          <li>
            <button
              type="button"
              className="mobile-nav-link mobile-docs-btn"
              aria-expanded={mobileDocsOpen}
              onClick={() => setMobileDocsOpen((o) => !o)}
            >
              Docs <ChevronDown size={18} className={`chev ${mobileDocsOpen ? 'open' : ''}`} />
            </button>
            {mobileDocsOpen && (
              <ul className="mobile-sub-list">
                {docs.map((d) => (
                  <li key={d.name}>
                    <Link to={d.path} className="mobile-sub-link" onClick={() => setIsMenuOpen(false)}>
                      <span>{d.name}</span>
                      <small>{d.desc}</small>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </li>
          <li>
            <Link
              to="/contact"
              className="mobile-nav-link contact-mobile"
              onClick={() => setIsMenuOpen(false)}
            >
              Contact Us
            </Link>
          </li>
        </ul>
      </div>
    </header>
  );
};

export default Navbar;
